/**
 * Images utils
 * @module utils/images-utils
 */

interface ScalingFactor {
  factor: number;
  quality: number;
}

interface DPRConfig {
  [dpr: string]: ScalingFactor;
}

interface AspectRatioConfig {
  [device: string]: DPRConfig;
}

const imageScalingFactors: Record<string, AspectRatioConfig> = {
  "3:1": {
    mobile: {
      "1x": { factor: 0.25, quality: 90 },
      "2x": { factor: 0.5, quality: 90 },
      "3x": { factor: 0.75, quality: 90 },
    },
    tablet: {
      "1x": { factor: 0.236, quality: 90 },
      "2x": { factor: 0.472, quality: 90 },
      "3x": { factor: 0.708, quality: 90 },
    },
    desktop: {
      "1x": { factor: 0.305, quality: 90 },
      "2x": { factor: 0.611, quality: 90 },
      "3x": { factor: 0.916, quality: 90 },
    },
    "4k": {
      "1x": { factor: 0.333, quality: 85 },
      "2x": { factor: 0.666, quality: 75 },
      "3x": { factor: 1.0, quality: 70 },
    },
  },
  "16:9": {
    mobile: {
      "1x": { factor: 0.111, quality: 90 },
      "2x": { factor: 0.222, quality: 90 },
      "3x": { factor: 0.333, quality: 90 },
    },
    tablet: {
      "1x": { factor: 0.167, quality: 90 },
      "2x": { factor: 0.333, quality: 90 },
      "3x": { factor: 0.5, quality: 90 },
    },
    desktop: {
      "1x": { factor: 0.267, quality: 90 },
      "2x": { factor: 0.533, quality: 90 },
      "3x": { factor: 0.8, quality: 90 },
    },
    "4k": {
      "1x": { factor: 0.333, quality: 85 },
      "2x": { factor: 0.666, quality: 75 },
      "3x": { factor: 1.0, quality: 65 },
    },
  },
  fallback: {
    mobile: {
      "1x": { factor: 0.111, quality: 90 },
      "2x": { factor: 0.222, quality: 90 },
      "3x": { factor: 0.333, quality: 90 },
    },
    tablet: {
      "1x": { factor: 0.167, quality: 90 },
      "2x": { factor: 0.333, quality: 90 },
      "3x": { factor: 0.5, quality: 90 },
    },
    desktop: {
      "1x": { factor: 0.267, quality: 90 },
      "2x": { factor: 0.533, quality: 90 },
      "3x": { factor: 0.8, quality: 90 },
    },
    "4k": {
      "1x": { factor: 0.333, quality: 85 },
      "2x": { factor: 0.666, quality: 75 },
      "3x": { factor: 1.0, quality: 65 },
    },
  },
};

/**
 * Get the aspect ratio of an image based on its width and height
 * @param {number} width
 * @param {number} height 
 * @returns 
 */
function getAspectRatio(width: number, height: number): string {
  const ratio = width / height;
  const rounded = Math.round(ratio * 100) / 100;
  if (Math.abs(rounded - 3.0) < 0.1) return "3:1";
  if (Math.abs(rounded - 16 / 9) < 0.1) return "16:9";
  return "fallback";
}

/**
 * Generate a sources for responsive images from constants by contentful url
 * @param {string} url - The contentful image url
 * @param {number} originWidth - The original width of the image
 * @param {number} originHeight - The original height of the image
 */
export function generateSources(
  url: string,
  originWidth: number,
  originHeight: number
) {
  const aspectRatio = getAspectRatio(originWidth, originHeight);
  const deviceConfig = imageScalingFactors[aspectRatio] || imageScalingFactors['fallback'];

  const sources = [];

  for (const [device, scales] of Object.entries(deviceConfig)) {
    let srcset = '';

    for (const [dpr, { factor, quality }] of Object.entries(scales)) {
      console.log(`Generating srcset for ${device} with dpr ${dpr} from ${originWidth}x${originHeight} and factor ${factor}`);
      const width = Math.round(originWidth * factor);
      const height = Math.round(originHeight * factor);
      const src = `${url}?w=${width}&h=${height}&fm=webp&q=${quality}`;
      srcset += `${src} ${dpr}, `;
    }

    console.log(`srcset for ${device}: ${srcset}`);

    sources.push({
      type: 'image/webp',
      media: device === 'mobile' ? `(max-width: 767px)` :
              device === 'tablet' ? `(min-width: 768px) and (max-width: 1023px)` :
              device === 'desktop' ? `(min-width: 1024px) and (max-width: 1439px)` :
              `(min-width: 1440px)`,
      srcset: srcset.slice(0, -2),
    });
  }

  return sources;
}